import { $ } from 'hanako-ts/dist/Framework';
import { Elem } from 'hanako-ts/dist/Collection/Types';
import { Collection } from 'hanako-ts/dist/Collection';

type pixelationDirection = 'in' | 'out' | 'full';

type HoverBlock = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export class Pixelator {
  private static instances: Array<Pixelator> = [];

  private img: HTMLImageElement;
  private callback: () => void;
  private callbackCalled: boolean;
  private clearAtEachStep: boolean;
  private width: number;
  private height: number;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private sourceCanvas: HTMLCanvasElement;
  private sourceCtx: CanvasRenderingContext2D;
  private pixelatedCanvas: HTMLCanvasElement;
  private pixelatedCtx: CanvasRenderingContext2D;
  private bandCanvas: HTMLCanvasElement;
  private bandCtx: CanvasRenderingContext2D;
  private imgData!: Uint8ClampedArray;
  private currentResolution!: number;
  private appearInterval: number | null;
  private hoverAnimationFrame: number | null;
  private forceSkipTimeout: number | null;
  private isInitialized: boolean;
  private maxResolution: number;
  private hoverBlocks: Array<HoverBlock> | null;
  private pixelatedCacheReady: boolean;

  constructor(img: Collection, callback: () => void, resolution?: number, fullscreen: boolean = false) {
    this.img = img.get(0) as HTMLImageElement;
    this.callback = callback;
    this.callbackCalled = false;
    this.appearInterval = null;
    this.hoverAnimationFrame = null;
    this.forceSkipTimeout = null;
    this.isInitialized = false;
    this.maxResolution = window.matchMedia('(pointer:fine)').matches ? 8 : 1;
    this.hoverBlocks = null;

    const srcPathname = new URL(this.img.src, window.location.href).pathname.toLowerCase();
    this.clearAtEachStep = /\.(png|webp)$/.test(srcPathname);

    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d')!;
    this.sourceCanvas = document.createElement('canvas');
    this.sourceCtx = this.sourceCanvas.getContext('2d')!;
    this.pixelatedCanvas = document.createElement('canvas');
    this.pixelatedCtx = this.pixelatedCanvas.getContext('2d')!;
    this.bandCanvas = document.createElement('canvas');
    this.bandCtx = this.bandCanvas.getContext('2d')!;
    this.pixelatedCacheReady = false;

    this.canvas.className = this.img.className;
    $(this.canvas).attr('data-appearing', '');
    this.canvas.id = this.img.id;
    this.canvas.width = this.width = Math.floor(this.img.width * window.devicePixelRatio);
    this.canvas.height = this.height = Math.floor(this.img.height * window.devicePixelRatio);

    if (!this.img.complete) {
      this.img.onload = () => {
        if (this.forceSkipTimeout !== null) {
          window.clearTimeout(this.forceSkipTimeout);
          this.forceSkipTimeout = null;
        }

        this.afterLoading(resolution, fullscreen);
      };

      this.forceSkipTimeout = window.setTimeout(() => {
        this.afterLoading(resolution, fullscreen);
      }, 25000);
    } else {
      if (!resolution) {
        this.afterLoading(resolution, fullscreen);
      }
    }

    if (!resolution) {
      $(this.canvas).on('mouseenter', () => {
        if (!this.isInitialized) return;

        this.startRendering('full');

        if (!this.callbackCalled) {
          this.callbackCalled = true;
          this.callback();
        }
      }).on('mouseleave', () => {
        if (!this.isInitialized) return;

        this.startRendering('out');
      });
    }

  }

  private afterLoading(resolution?: number, fullscreen: boolean = false) {
    if (this.isInitialized) return;
    this.isInitialized = true;
    this.img.onload = null;

    if (this.forceSkipTimeout !== null) {
      window.clearTimeout(this.forceSkipTimeout);
      this.forceSkipTimeout = null;
    }

    this.sourceCanvas.width = this.width;
    this.sourceCanvas.height = this.height;
    this.sourceCtx.drawImage(this.img, 0, 0, this.width, this.height);
    this.imgData = this.sourceCtx.getImageData(0, 0, this.width, this.height).data;

    this.ctx.clearRect(0, 0, this.width, this.height);

    this.img.parentNode?.replaceChild(this.canvas, this.img);

    this.currentResolution = Math.pow(2, Math.floor(Math.log2(this.width / 2)));

    this.render();

    setTimeout(() => {
      if (resolution) {
        this.renderAtResolution(resolution);

        if (fullscreen) {
          this.canvas.classList.add('h-screen', 'object-cover');
        }
      } else {

        this.startRendering();
      }
    }, 125);
  }

  private startRendering(direction: pixelationDirection = 'in') {
    this.stopHoverAnimation();

    if (this.appearInterval !== null) {
      window.clearInterval(this.appearInterval);
      this.appearInterval = null;
    }

    if (direction === 'full') {
      this.startNoiseReveal();
      return;
    }

    if (direction === 'out') {
      this.startNoiseHide();
      return;
    }

    this.appearInterval = window.setInterval(() => {
      this.render(direction);
    }, 100);
  }

  private stopHoverAnimation() {
    if (this.hoverAnimationFrame !== null) {
      window.cancelAnimationFrame(this.hoverAnimationFrame);
      this.hoverAnimationFrame = null;
    }
  }

  private startNoiseReveal() {
    const duration = 450;
    const startTime = performance.now();
    const blocks = this.getHoverBlocks();

    const animate = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);

      this.renderNoiseReveal(blocks, progress);

      if (progress < 1) {
        this.hoverAnimationFrame = window.requestAnimationFrame(animate);
      } else {
        this.hoverAnimationFrame = null;
      }
    };

    this.hoverAnimationFrame = window.requestAnimationFrame(animate);
  }

  private startNoiseHide() {
    const duration = 450;
    const startTime = performance.now();
    const blocks = this.hoverBlocks ?? this.getHoverBlocks();

    this.ensurePixelatedCache();

    const animate = (now: number) => {
      const progress = Math.min(1, (now - startTime) / duration);

      this.renderNoiseHide(blocks, progress);

      if (progress < 1) {
        this.hoverAnimationFrame = window.requestAnimationFrame(animate);
      } else {
        this.hoverAnimationFrame = null;
        this.hoverBlocks = null;
      }
    };

    this.hoverAnimationFrame = window.requestAnimationFrame(animate);
  }

  private renderNoiseReveal(blocks: Array<HoverBlock>, progress: number) {
    const visibleCount = Math.max(0, Math.ceil(blocks.length * progress));

    this.ctx.clearRect(0, 0, this.width, this.height);
    this.renderAtResolution(this.maxResolution);

    for (let i = 0; i < visibleCount; i += 1) {
      const block = blocks[i];

      this.ctx.drawImage(this.sourceCanvas, block.x, block.y, block.width, block.height, block.x, block.y, block.width, block.height);
    }
  }

  private renderNoiseHide(blocks: Array<HoverBlock>, progress: number) {
    const visibleCount = Math.max(0, Math.ceil(blocks.length * progress));

    this.ctx.drawImage(this.sourceCanvas, 0, 0, this.width, this.height);

    for (let i = 0; i < visibleCount; i += 1) {
      const block = blocks[blocks.length - 1 - i];

      this.ctx.drawImage(this.pixelatedCanvas, block.x, block.y, block.width, block.height, block.x, block.y, block.width, block.height);
    }
  }

  private ensurePixelatedCache() {
    if (this.pixelatedCacheReady) return;

    this.pixelatedCanvas.width = this.width;
    this.pixelatedCanvas.height = this.height;
    this.pixelatedCtx.clearRect(0, 0, this.width, this.height);

    if (this.maxResolution <= 1) {
      this.pixelatedCtx.drawImage(this.sourceCanvas, 0, 0, this.width, this.height);
      this.pixelatedCacheReady = true;
      return;
    }

    const reducedWidth = Math.max(1, Math.ceil(this.width / this.maxResolution));
    const reducedHeight = Math.max(1, Math.ceil(this.height / this.maxResolution));

    this.bandCanvas.width = reducedWidth;
    this.bandCanvas.height = reducedHeight;
    this.bandCtx.clearRect(0, 0, reducedWidth, reducedHeight);
    this.bandCtx.imageSmoothingEnabled = false;
    this.bandCtx.drawImage(this.sourceCanvas, 0, 0, this.width, this.height, 0, 0, reducedWidth, reducedHeight);

    this.pixelatedCtx.imageSmoothingEnabled = false;
    this.pixelatedCtx.drawImage(this.bandCanvas, 0, 0, reducedWidth, reducedHeight, 0, 0, this.width, this.height);

    this.pixelatedCacheReady = true;
  }

  private getHoverBlocks(): Array<HoverBlock> {
    if (this.hoverBlocks !== null) return this.hoverBlocks;

    const blockSize = Math.max(1, Math.round((this.maxResolution === 8 ? 8 : 8) * window.devicePixelRatio));
    const blocks: Array<HoverBlock> = [];

    for (let y = 0; y < this.height; y += blockSize) {
      for (let x = 0; x < this.width; x += blockSize) {
        blocks.push({
          x,
          y,
          width: Math.min(blockSize, this.width - x),
          height: Math.min(blockSize, this.height - y),
        });
      }
    }

    for (let i = blocks.length - 1; i > 0; i -= 1) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      const temp = blocks[i];
      blocks[i] = blocks[randomIndex];
      blocks[randomIndex] = temp;
    }

    this.hoverBlocks = blocks;

    return blocks;
  }

  private render(direction: pixelationDirection = 'in') {
    if (direction === 'full') {
      this.ctx.drawImage(this.sourceCanvas, 0, 0, this.width, this.height);
      return;
    }

    const resolution: number = (direction === 'out') ? this.maxResolution : this.currentResolution;

    if (direction === 'in') {
      this.currentResolution -= this.currentResolution / 2;
    } else {
      this.currentResolution += this.currentResolution;
    }

    if (this.clearAtEachStep) this.ctx.clearRect(0, 0, this.width, this.height);

    this.renderAtResolution(resolution);

    if ((resolution <= this.maxResolution && direction === 'in') || (resolution >= 8 && direction === 'out')) {
      if (this.appearInterval !== null) {
        window.clearInterval(this.appearInterval);
        this.appearInterval = null;
      }

      if (['in', 'out'].includes(direction)) this.currentResolution = this.maxResolution / 2 < 1 ? 1 : this.maxResolution / 2;

      if (direction === 'in' && !this.callbackCalled) {
        $(this.img).attr('data-appeared', '');
        $(this.canvas).attr('data-appeared', '');

        this.callbackCalled = true;
        this.ensurePixelatedCache();

        this.callback();
      }

      return;
    }
  }

  public renderAtResolution(resolution: number) {
    let pixelIndex: number;
    let red: number;
    let green: number;
    let blue: number;
    let pixelAlpha: number;

    for (let y = 0; y < this.height / resolution; y++) {
      for (let x = 0; x < this.width / resolution; x++) {
        const sampleX = Math.min(this.width - 1, Math.floor((x + 0.5) * resolution));
        const sampleY = Math.min(this.height - 1, Math.floor((y + 0.5) * resolution));

        pixelIndex = (sampleX + (sampleY * this.width)) * 4;
        red = this.imgData[pixelIndex + 0];
        green = this.imgData[pixelIndex + 1];
        blue = this.imgData[pixelIndex + 2];
        pixelAlpha = this.imgData[pixelIndex + 3] / 255;

        this.ctx.fillStyle = 'rgba(' + red + ',' + green + ',' + blue + ',' + pixelAlpha + ')';
        const cellX = Math.floor(x * resolution);
        const cellY = Math.floor(y * resolution);
        const nextCellX = Math.min(this.width, Math.floor((x + 1) * resolution));
        const nextCellY = Math.min(this.height, Math.floor((y + 1) * resolution));
        const cellW = Math.max(1, nextCellX - cellX);
        const cellH = Math.max(1, nextCellY - cellY);

        this.ctx.fillRect(cellX, cellY, cellW, cellH);
      }
    }
  }

  public static createPixelator(img: Collection, callback: () => void): void {
    const pixelator: Pixelator = new Pixelator(img, callback);
    Pixelator.instances.push(pixelator);
  }

  public static changePixelation(id: string, direction: pixelationDirection): void {
    const pixelator: Pixelator | undefined = Pixelator.instances.find((instance: Pixelator) => {
      return instance.canvas.id === id;
    });

    if (!pixelator) return;

    pixelator.startRendering(direction);
  }
}
